import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from '@/i18n'
import clsx from 'clsx'

export interface AgentOption {
  id: string
  name: string
  label: string
  description: string
  color: 'blue' | 'green' | 'amber' | 'purple' | 'orange'
  avatarUrl?: string
}

interface AgentMentionAutocompleteProps {
  isVisible: boolean
  selectedIndex: number
  filterQuery: string
  onSelect: (agent: AgentOption) => void
  onClose: () => void
  /** If provided, only show agents whose store ID is in this set */
  availableAgentIds?: string[]
}

/**
 * Complete agent registry keyed by store ID.
 * This is the single source of truth for @ mention options.
 */
const AGENT_REGISTRY: Record<string, AgentOption> = {
  'plant-support': {
    id: 'plant_support_engineer',
    name: '@ai-plant-support-engineer',
    label: 'AI Plant Support Engineer',
    description: 'Plant Support Engineer',
    color: 'amber',
    avatarUrl: '/images/optimized/plant-support-engineer-avatar.webp',
  },
  'chemical-agent': {
    id: 'chemical_engineer',
    name: '@ai-chemical-engineer',
    label: 'AI Chemical Engineer',
    description: 'Chemical Engineering Expert',
    color: 'blue',
    avatarUrl: '/images/optimized/chemical-agent-avatar.webp',
  },
  'mechanical-agent': {
    id: 'mechanical_engineer',
    name: '@ai-mechanical-engineer',
    label: 'AI Mechanical Engineer',
    description: 'Mechanical Engineering Expert',
    color: 'green',
    avatarUrl: '/images/optimized/mechanical-agent-avatar.webp',
  },
  'electrical-agent': {
    id: 'electrical_engineer',
    name: '@ai-electrical-engineer',
    label: 'AI Electrical Engineer',
    description: 'Electrical Engineering Expert',
    color: 'purple',
    avatarUrl: '/images/optimized/electrical-agent-avatar.webp',
  },
  'epc-agent': {
    id: 'epc_engineer',
    name: '@ai-epc-engineer',
    label: 'AI EPC Engineer',
    description: 'EPC Engineering Expert',
    color: 'orange',
    avatarUrl: '/images/epc-agent.png',
  },
  'maint-agent': {
    id: 'maintenance_engineer',
    name: '@ai-maintenance-engineer',
    label: 'AI Maintenance & Repair Engineer',
    description: 'Maintenance & Repair Engineering Expert',
    color: 'purple',
    avatarUrl: '/images/repair-agent.png',
  },
}

/** Default display order when no availability list is provided */
const DEFAULT_AGENTS: AgentOption[] = Object.values(AGENT_REGISTRY)

// Legacy export — kept for backwards compatibility with ChatInterface's handleAgentSelect
const AGENTS = DEFAULT_AGENTS

// Fuzzy matching function for filtering agents
const fuzzyMatch = (query: string, text: string): boolean => {
  const queryLower = query.toLowerCase()
  const textLower = text.toLowerCase()

  // Exact substring match
  if (textLower.includes(queryLower)) return true

  // Fuzzy match - check if all query characters appear in order
  let textIndex = 0
  for (let i = 0; i < queryLower.length; i++) {
    const charIndex = textLower.indexOf(queryLower[i], textIndex)
    if (charIndex === -1) return false
    textIndex = charIndex + 1
  }
  return true
}

export const AgentMentionAutocomplete = ({
  isVisible,
  selectedIndex,
  filterQuery,
  onSelect,
  onClose,
  availableAgentIds,
}: AgentMentionAutocompleteProps) => {
  const { t, i18n } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const selectedRowRef = useRef<HTMLButtonElement>(null)

  // The agent list scrolls (max-h-64) — keep the arrow-key selection visible.
  useEffect(() => {
    selectedRowRef.current?.scrollIntoView({ block: 'nearest' })
  }, [selectedIndex])

  // Build available agents from the store agent IDs.
  // Semantics:
  //   undefined -> caller did not filter, show every agent we know about
  //   []        -> caller explicitly says "no agents are available right now",
  //                show none (this is what stops disabled agents like a
  //                toggled-off Chemical Engineer from appearing in @-mentions)
  //   [...ids]  -> show only those ids
  const availableAgents = useMemo(
    () => {
      if (availableAgentIds === undefined) return DEFAULT_AGENTS
      return availableAgentIds
        .map((storeId) => AGENT_REGISTRY[storeId])
        .filter(Boolean) as AgentOption[]
    },
    [availableAgentIds],
  )

  const filteredAgents = availableAgents.filter((agent) => {
    if (!filterQuery) return true

    const queryLower = filterQuery.toLowerCase()

    if (agent.id.toLowerCase().includes(queryLower) ||
        agent.name.toLowerCase().includes(queryLower)) {
      return true
    }

    return fuzzyMatch(filterQuery, agent.label)
  })

  // Build dynamic keywords hint from available agents via i18n
  const tryKeywordsHint = useMemo(() => {
    if (availableAgents.length === 0) return ''
    const keywords = availableAgents.map(
      (a) => `"${t(`chat:mention.search_keywords.${a.id}`)}"`,
    )
    if (keywords.length === 1) return keywords[0]
    try {
      return new Intl.ListFormat(i18n.language, { style: 'long', type: 'disjunction' }).format(keywords)
    } catch {
      return keywords.join(', ')
    }
  }, [availableAgents, t, i18n.language])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isVisible, onClose])

  if (!isVisible) return null

  const getColorClasses = (color: string, isSelected: boolean) => {
    if (color === 'blue') {
      return {
        bg: isSelected ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-blue-50 dark:bg-blue-900/20',
        text: 'text-blue-700 dark:text-blue-400',
        border: 'border-blue-200 dark:border-blue-800',
      }
    }
    if (color === 'amber') {
      return {
        bg: isSelected ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-amber-50 dark:bg-amber-900/20',
        text: 'text-amber-700 dark:text-amber-400',
        border: 'border-amber-200 dark:border-amber-800',
      }
    }
    if (color === 'purple') {
      return {
        bg: isSelected ? 'bg-purple-100 dark:bg-purple-900/30' : 'bg-purple-50 dark:bg-purple-900/20',
        text: 'text-purple-700 dark:text-purple-400',
        border: 'border-purple-200 dark:border-purple-800',
      }
    }
    if (color === 'orange') {
      return {
        bg: isSelected ? 'bg-orange-100 dark:bg-orange-900/30' : 'bg-orange-50 dark:bg-orange-900/20',
        text: 'text-orange-700 dark:text-orange-400',
        border: 'border-orange-200 dark:border-orange-800',
      }
    }
    return {
      bg: isSelected ? 'bg-green-100 dark:bg-green-900/30' : 'bg-green-50 dark:bg-green-900/20',
      text: 'text-green-700 dark:text-green-400',
      border: 'border-green-200 dark:border-green-800',
    }
  }

  return (
    <div
      ref={containerRef}
      className="absolute bottom-full left-0 mb-2 w-full max-w-md bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border rounded-lg shadow-lg z-50 overflow-hidden"
    >
      <div className="px-3 py-2 bg-gray-50 dark:bg-dark-muted border-b border-gray-200 dark:border-dark-border">
        <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
          {filterQuery
            ? `${t('chat:mention.hint')} (filtering: "${filterQuery}")`
            : t('chat:mention.hint')}
        </p>
      </div>
      {/* max-h + scroll (chatV2 parity): with 6 registered agents the menu is
          taller than the space above a low-sitting composer, which clipped
          rows at the top and the hint header off-screen. The 40vh arm keeps
          the whole menu inside short hosts (resized board panel) too. */}
      <div className="py-1 max-h-[min(16rem,40vh)] overflow-y-auto">
        {filteredAgents.length === 0 ? (
          <div className="px-4 py-6 text-center">
            <p className="text-sm text-gray-500 dark:text-dark-text-muted">
              {t('chat:mention.autocomplete_no_results', { query: filterQuery })}
            </p>
            {tryKeywordsHint && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                {t('chat:mention.autocomplete_try', { keywords: tryKeywordsHint })}
              </p>
            )}
          </div>
        ) : (
          filteredAgents.map((agent, index) => {
            const colors = getColorClasses(agent.color, selectedIndex === index)
            return (
            <button
              key={agent.id}
              ref={selectedIndex === index ? selectedRowRef : undefined}
              onClick={() => onSelect(agent)}
              className={clsx(
                'w-full px-4 py-3 flex items-center gap-3 transition-colors',
                selectedIndex === index
                  ? 'bg-gray-100 dark:bg-dark-accent'
                  : 'hover:bg-gray-50 dark:hover:bg-dark-muted'
              )}
            >
              <div className="relative w-8 h-8 rounded-full flex-shrink-0">
                {agent.avatarUrl ? (
                  <>
                    <img
                      src={agent.avatarUrl}
                      alt={agent.label}
                      className="w-8 h-8 rounded-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                        const fallback = target.nextElementSibling as HTMLElement
                        if (fallback) fallback.classList.remove('hidden')
                      }}
                    />
                    {/* Fallback letter avatar */}
                    <div
                      className={clsx(
                        'hidden w-8 h-8 rounded-full flex items-center justify-center',
                        'font-mono font-semibold text-sm border absolute inset-0',
                        colors.bg,
                        colors.text,
                        colors.border
                      )}
                    >
                      {agent.label.charAt(0)}
                    </div>
                  </>
                ) : (
                  <div
                    className={clsx(
                      'w-8 h-8 rounded-full flex items-center justify-center',
                      'font-mono font-semibold text-sm border',
                      colors.bg,
                      colors.text,
                      colors.border
                    )}
                  >
                    {agent.label.charAt(0)}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-start flex-1">
                <span className="text-sm font-medium text-gray-900 dark:text-dark-text">
                  {agent.label}
                </span>
                <span className="text-xs text-gray-500 dark:text-dark-text-muted">
                  Type: {agent.name}
                </span>
              </div>
              {selectedIndex === index && (
                <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
                  <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-[10px] font-mono">
                    ↵
                  </kbd>
                </div>
              )}
            </button>
          )
        })
        )}
      </div>
      <div className="px-3 py-2 bg-gray-50 dark:bg-dark-muted border-t border-gray-200 dark:border-dark-border flex items-center justify-between">
        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-dark-text-muted">
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-white dark:bg-dark-card border border-gray-300 dark:border-gray-600 rounded text-[10px] font-mono">
              ↑↓
            </kbd>
            <span>Navigate</span>
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-white dark:bg-dark-card border border-gray-300 dark:border-gray-600 rounded text-[10px] font-mono">
              ↵
            </kbd>
            <span>Select</span>
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-white dark:bg-dark-card border border-gray-300 dark:border-gray-600 rounded text-[10px] font-mono">
              Esc
            </kbd>
            <span>Close</span>
          </span>
        </div>
      </div>
    </div>
  )
}

export { AGENTS, AGENT_REGISTRY }
